import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { HandCoins, Plus, Wallet, Download, Mail, Eye, Copy } from "lucide-react";
import api, { errMsg, downloadBlob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProjects } from "@/context/ProjectContext";
import { PageHeader, SectionCard, Loading, EmptyState, Money } from "@/components/Common";
import { DisbursementProjectField } from "@/components/DisbursementProjectField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { formatTanggal, formatTanggalWaktu } from "@/lib/format";
import { toast } from "sonner";

function yearStart() { return `${new Date().getFullYear()}-01-01`; }
function today() { return new Date().toISOString().slice(0, 10); }

export default function PencairanInvestor() {
  const { isSuperadmin } = useAuth();
  const { projects = [], projectId, projectParam } = useProjects();
  const projName = projectId !== "all" ? projects.find((p) => p.id === projectId)?.name : null;
  const [from, setFrom] = useState(yearStart());
  const [to, setTo] = useState(today());

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["disbursement-summary", from, to, projectId],
    queryFn: async () => (await api.get("/investor-disbursements/summary", { params: { date_from: from, date_to: to, ...projectParam } })).data,
  });
  const { data: recent = [], refetch: refetchRecent } = useQuery({
    queryKey: ["disbursements", from, to, projectId],
    queryFn: async () => (await api.get("/investor-disbursements", { params: { date_from: from, date_to: to, ...projectParam } })).data,
  });

  const { data: sendLog = [], refetch: refetchLog } = useQuery({
    queryKey: ["send-log"],
    queryFn: async () => (await api.get("/investor-disbursements/send-log")).data,
    enabled: isSuperadmin,
  });

  const reload = () => { refetch(); refetchRecent(); if (isSuperadmin) refetchLog(); };
  const [sending, setSending] = useState(false);

  const copyRek = async (no) => {
    try { await navigator.clipboard.writeText(no); toast.success("Nomor rekening disalin."); }
    catch { toast.error("Gagal menyalin. Salin manual."); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/investor-disbursements/export.csv", { params: { date_from: from, date_to: to, ...projectParam }, responseType: "blob" });
      downloadBlob(res.data, "rekap-pencairan-investor.csv");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportXlsx = async () => {
    try {
      const res = await api.get("/investor-disbursements/export.xlsx", { params: { date_from: from, date_to: to, ...projectParam }, responseType: "blob" });
      downloadBlob(res.data, "rekap-pencairan-investor.xlsx");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sendSummary = async () => {
    setSending(true);
    try { await api.post("/investor-disbursements/send-summary"); toast.success("Ringkasan terkirim ke email superadmin."); refetchLog(); }
    catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };

  return (
    <div className="space-y-5">
      <PageHeader title={projName ? `Pencairan Investor — ${projName}` : "Pencairan Investor"} description={projName ? `Hak & pencairan investor khusus proyek ${projName}. Ganti proyek lewat pilihan di bagian atas.` : (isSuperadmin ? "Ringkasan hak investor vs dana yang sudah ditransfer (semua proyek). Pilih proyek di bagian atas untuk melihat per proyek." : "Hak & pencairan investor yang memiliki rumah di proyek Anda.")}
        actions={<div className="flex flex-wrap gap-2">
          {isSuperadmin && <PreviewDialog />}
          {isSuperadmin && <Button variant="outline" onClick={sendSummary} disabled={sending} data-testid="pencairan-send-summary"><Mail className="mr-2 h-4 w-4" /> Kirim Ringkasan</Button>}
          <Button variant="outline" onClick={exportCsv} data-testid="pencairan-export-csv"><Download className="mr-2 h-4 w-4" /> Ekspor CSV</Button>
          <Button variant="outline" onClick={exportXlsx} data-testid="pencairan-export-xlsx"><Download className="mr-2 h-4 w-4" /> Ekspor Excel</Button>
          <RecordDialog onDone={reload} isSuperadmin={isSuperadmin} fixedProjectId={projectId !== "all" ? projectId : null} />
        </div>} />

      <SectionCard testid="pencairan-filter">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div><Label className="text-xs">Dari (tgl)</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="pencairan-from" /></div>
          <div><Label className="text-xs">Sampai</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="pencairan-to" /></div>
          <div className="flex items-end"><Button onClick={reload} className="w-full" data-testid="pencairan-apply">Terapkan</Button></div>
        </div>
      </SectionCard>

      {isLoading ? <Loading /> : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <SectionCard title="Total Hak Investor"><div className="text-2xl font-extrabold"><Money value={data?.total_entitlement || 0} /></div></SectionCard>
            <SectionCard title="Total Sudah Dicairkan"><div className="text-2xl font-extrabold"><Money value={data?.total_disbursed || 0} /></div></SectionCard>
            <SectionCard title="Total Belum Dicairkan"><div className="text-2xl font-extrabold text-primary"><Money value={data?.total_outstanding || 0} /></div></SectionCard>
          </div>

          <SectionCard title="Per Investor" testid="pencairan-table">
            {(data?.rows || []).length === 0 ? (
              <EmptyState title="Belum ada data" description="Tidak ada hak maupun pencairan pada rentang ini." icon={Wallet} />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Investor</TableHead>
                    <TableHead>Rekening Bank</TableHead>
                    <TableHead className="text-right">Hak (80%)</TableHead>
                    <TableHead className="text-right">Dicairkan</TableHead>
                    <TableHead className="text-right">Belum Dicairkan</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.rows.map((r) => (
                      <TableRow key={r.investor_id} data-testid={`pencairan-row-${r.investor_id}`}>
                        <TableCell className="font-medium">{r.investor_name}</TableCell>
                        <TableCell>
                          {r.bank_account_no ? (
                            <div className="flex items-center gap-2">
                              <div className="text-xs leading-tight">
                                <div className="font-medium text-foreground">{r.bank_name || "Bank"}</div>
                                <div className="font-mono" data-testid={`pencairan-rek-${r.investor_id}`}>{r.bank_account_no}</div>
                                {r.bank_account_holder && <div className="text-muted-foreground">a.n. {r.bank_account_holder}</div>}
                              </div>
                              <Button size="sm" variant="ghost" className="h-7 px-2" title="Salin nomor rekening" onClick={() => copyRek(r.bank_account_no)} data-testid={`pencairan-copy-${r.investor_id}`}><Copy className="h-3.5 w-3.5" /></Button>
                            </div>
                          ) : <span className="text-xs text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell className="text-right"><Money value={r.entitlement} /></TableCell>
                        <TableCell className="text-right text-muted-foreground"><Money value={r.disbursed} /></TableCell>
                        <TableCell className="text-right font-semibold" style={{ color: r.outstanding > 0 ? "#c2410c" : "#15803d" }}><Money value={r.outstanding} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Riwayat Pencairan" description="Transfer yang tercatat pada rentang ini.">
            {recent.length === 0 ? (
              <EmptyState title="Belum ada pencairan tercatat" icon={HandCoins} />
            ) : (
              <div className="space-y-1.5">
                {recent.map((d) => (
                  <div key={d.id} className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2 text-sm" data-testid={`disbursement-item-${d.id}`}>
                    <span>{formatTanggal(d.date)} · <b>{d.investor_name}</b> · {d.project_name && d.project_name !== "-" ? `${d.project_name} · ` : ""}{d.method_label || d.method} · {d.reference || "tanpa referensi"}</span>
                    <Money value={d.amount} />
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          {isSuperadmin && <SectionCard title="Riwayat Pengiriman Ringkasan" description="Kapan ringkasan terakhir dikirim (manual & terjadwal).">
            {sendLog.length === 0 ? (
              <EmptyState title="Belum ada pengiriman" icon={Mail} />
            ) : (
              <div className="space-y-1.5">
                {sendLog.map((l) => (
                  <div key={l.id} className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2 text-sm" data-testid={`send-log-${l.id}`}>
                    <span>{formatTanggalWaktu(l.sent_at)} · {l.trigger === "scheduled" ? "Terjadwal" : "Manual"} · {l.channel} · oleh {l.actor}</span>
                    <span className="text-xs text-muted-foreground">periode {l.period}</span>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>}
        </>
      )}
    </div>
  );
}

function PreviewDialog() {
  const [open, setOpen] = useState(false);
  const { data } = useQuery({ queryKey: ["summary-preview", open], queryFn: async () => (await api.get("/investor-disbursements/summary-preview")).data, enabled: open });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="pencairan-preview"><Eye className="mr-2 h-4 w-4" /> Pratinjau</Button></DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Pratinjau Email Ringkasan</DialogTitle></DialogHeader>
        {data?.subject && <div className="text-sm"><span className="text-muted-foreground">Subjek: </span><span className="font-medium">{data.subject}</span></div>}
        <div className="max-h-[60vh] overflow-auto rounded-lg border border-border">
          {data?.html ? <iframe title="preview-email" srcDoc={data.html} sandbox="" className="h-[420px] w-full bg-white" data-testid="preview-iframe" /> : <div className="p-6 text-sm text-muted-foreground">Memuat pratinjau…</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RecordDialog({ onDone, isSuperadmin, fixedProjectId }) {
  const [open, setOpen] = useState(false);
  const blank = { investor_id: "", project_id: "", amount: "", date: new Date().toISOString().slice(0, 10), method: "transfer", reference: "", note: "" };
  const [f, setF] = useState(blank);
  const { data: allInvestors = [] } = useQuery({
    queryKey: ["investors", fixedProjectId || "all"],
    queryFn: async () => (await api.get("/investors", { params: fixedProjectId ? { project_id: fixedProjectId } : {} })).data,
    enabled: open,
  });
  const investors = allInvestors.filter((i) => fixedProjectId ? (i.house_project_ids || []).includes(fixedProjectId) : (isSuperadmin || (i.house_project_ids || []).length > 0));
  const selInv = investors.find((i) => i.id === f.investor_id);
  const save = async () => {
    if (!f.investor_id) { toast.error("Pilih investor."); return; }
    if (!fixedProjectId && (selInv?.project_ids || []).length > 1 && !f.project_id) { toast.error("Pilih proyek."); return; }
    if (!Number(f.amount)) { toast.error("Nominal wajib diisi."); return; }
    try {
      await api.post("/investor-disbursements", { investor_id: f.investor_id, project_id: fixedProjectId || f.project_id || undefined, amount: Number(f.amount), date: f.date, method: f.method, reference: f.reference, note: f.note });
      toast.success("Pencairan tercatat."); setOpen(false); setF(blank); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="record-disbursement-button"><Plus className="mr-2 h-4 w-4" /> Catat Pencairan</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Catat Pencairan ke Investor</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Investor</Label>
            <Select value={f.investor_id} onValueChange={(v) => setF({ ...f, investor_id: v, project_id: "" })}>
              <SelectTrigger data-testid="record-investor"><SelectValue placeholder="Pilih investor" /></SelectTrigger>
              <SelectContent>{investors.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
            </Select></div>
          {selInv && (selInv.bank_account_no ? (
            <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-muted/40 p-2 text-xs" data-testid="record-rek-box">
              <div><span className="font-medium">{selInv.bank_name || "Bank"}</span> · <span className="font-mono">{selInv.bank_account_no}</span>{selInv.bank_account_holder ? ` · a.n. ${selInv.bank_account_holder}` : ""}</div>
              <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={() => { navigator.clipboard.writeText(selInv.bank_account_no); toast.success("Nomor rekening disalin."); }} data-testid="record-copy-rek"><Copy className="h-3.5 w-3.5" /></Button>
            </div>
          ) : <div className="text-xs text-muted-foreground">Investor ini belum memiliki data rekening (lengkapi di Pengaturan).</div>)}
          <DisbursementProjectField investor={selInv} value={f.project_id} onChange={(v) => setF({ ...f, project_id: v })} fixedProjectId={fixedProjectId} />
          <div><Label>Nominal</Label><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="record-amount" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Tanggal</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="record-date" /></div>
            <div><Label>Metode</Label>
              <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}><SelectTrigger data-testid="record-method"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="transfer">Transfer</SelectItem><SelectItem value="cash">Tunai</SelectItem></SelectContent></Select></div>
          </div>
          <div><Label>Referensi</Label><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} data-testid="record-reference" /></div>
          <div><Label>Catatan</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="record-note" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} data-testid="record-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
